import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import PageHero from "@/components/shared/PageHero";
import { useLang } from "@/i18n/LanguageContext";
import { MOCK_NEWS } from "@/data/ekipData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Actualites() {
  const { t, lang } = useLang();
  const [items, setItems] = useState(MOCK_NEWS);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    axios
      .get(`${API}/news`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) setItems(r.data);
      })
      .catch(() => {
        // fallback to mock
      });
  }, []);

  const filtered = category === "all" ? items : items.filter((n) => n.category === category);

  return (
    <div data-testid="actualites-page">
      <PageHero
        overline={t.actualites.hero.overline}
        title={t.actualites.hero.title}
        titleItalic={t.actualites.hero.titleItalic}
      />

      <section className="bg-ekip-cream py-20 md:py-24" data-testid="actualites-list">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-12">
            {["all", "communique", "media", "evenement", "publication"].map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`actualites-filter-${c}`}
                className={`text-[11px] uppercase tracking-[0.24em] font-medium px-4 py-2 border transition-colors ${
                  category === c
                    ? "bg-ekip-navy text-white border-ekip-navy"
                    : "bg-white text-ekip-navy border-ekip-gold/30 hover:border-ekip-copper hover:text-ekip-copper"
                }`}
              >
                {t.actualites.categories[c]}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className="text-center py-24 text-ekip-graphite font-body font-light">
              {t.actualites.empty}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((n, i) => (
                <motion.article
                  key={n.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: (i % 6) * 0.06 }}
                  data-testid={`actualites-item-${n.slug}`}
                  className="group border border-ekip-gold/20 bg-white overflow-hidden hover:border-ekip-copper transition-colors flex flex-col"
                >
                  <div className="relative h-56 overflow-hidden">
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                      style={{ backgroundImage: `url(${n.image})` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ekip-navy/50 to-transparent" />
                    <span className="absolute top-4 left-4 inline-flex items-center gap-2 bg-ekip-navy text-white px-3 py-1 text-[10px] uppercase tracking-[0.22em]">
                      {t.actualites.categories[n.category] || n.category}
                    </span>
                  </div>
                  <div className="p-6 md:p-8 flex-1 flex flex-col">
                    <time className="text-[10px] uppercase tracking-[0.25em] text-ekip-graphite">
                      {new Date(n.date).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </time>
                    <h3 className="mt-3 font-display font-semibold text-lg md:text-xl text-ekip-navy leading-snug">
                      {typeof n.title === "object" ? n.title[lang] : n.title}
                    </h3>
                    <p className="mt-3 text-sm text-ekip-graphite font-body font-light leading-relaxed flex-1">
                      {typeof n.excerpt === "object" ? n.excerpt[lang] : n.excerpt}
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-ekip-copper font-medium">
                      {t.common.readMore} →
                    </span>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
